// ABOUTME: The one owner of the calendar window's cache records: every load of them — the page's own
// ABOUTME: group prefetch, its periodic tick and the realtime refresh — is queued here and stays ordered.
import { useSyncExternalStore } from "react"
import { getWeekDates } from "@/model/calendar/weekDates"
import { overlapsDays } from "@/model/calendar/cache/calendarRange"
import type { CalendarScope } from "@/model/calendar/cache/recordOwners"
import { registerRecordOwner } from "@/model/calendar/cache/recordOwners"
import type { RefreshOutcome } from "@/model/calendar/cache/refreshOutcome"
import { ABORTED, NOTHING_TO_DO, refreshFailed } from "@/model/calendar/cache/refreshOutcome"
import { runSerial } from "@/model/calendar/cache/runSerial"
import { dateSpan } from "@/model/date"

/**
 * The invariant this holds, for the per-week records a mounted window loads
 * (`calendarItems(dates,userID)`, `basicRequirementsBatch(requests)`,
 * `healthMetricsSummary(window)`):
 *
 * EVERY refresh of them goes through this owner and is serialized — the page's
 * scroll prefetch, its periodic tick, a realtime event, the refresh button, and
 * the refetch that follows a mutation (refreshAfterMutation). No two of them
 * are ever in flight together, so an older answer cannot land on a newer one.
 *
 * Two kinds of write deliberately do not queue here. Optimistic writes (drag
 * and drop, card edits, the undo hide) are synchronous by design and are
 * superseded by the serialized refresh that follows them. Cache-first first
 * fetches write a record only when it is absent, so they have nothing to
 * overwrite.
 */

/** The weeks on screen, whose they are, and how to load a set of them. */
export interface CalendarWindowRegistration {
  userID: string
  /** Inclusive `yyyy-MM-dd` bounds of the weeks the window holds. */
  from: string
  to: string
  /** Every week the window shows — what a full refresh loads. */
  weeks: readonly Date[]
  /** Whether these are the signed-in user's own records, which some queries key without a userID. */
  isSelf: boolean
  /**
   * Loads exactly these weeks through the consolidated prefetch. `isStale`
   * turns true if this load outlives the runner's stall timeout and a
   * successor takes over — the load must then write nothing (runSerial).
   */
  load: (weeks: readonly Date[], isStale: () => boolean) => Promise<RefreshOutcome>
}

/**
 * Module state for the same reason as batchChunkRefetch: the realtime provider
 * sits above the calendar page, and the page alone knows which weeks are on
 * screen and who owns them (see useCalendarPage).
 */
/**
 * Every mounted window, newest last — not a single slot, for the same reason
 * registerBatchChunkRefetch keeps a stack: two can briefly coexist while a tree
 * is rebuilt, and the second one's unmount would otherwise unregister the
 * survivor.
 */
const windows: CalendarWindowRegistration[] = []
const currentWindow = (): CalendarWindowRegistration | null => windows.at(-1) ?? null

const listeners = new Set<() => void>()

/**
 * Loads of ONE calendar never overlap. Three writers of the same per-week
 * records — the page's group prefetch on mount and scroll, its periodic tick,
 * and the refresh a realtime event triggers — could otherwise be in flight
 * together, and the older answer landing second would overwrite the change that
 * triggered the newer one.
 *
 * Per owner, though: two athletes share no cache records, so a coach switching
 * to the next athlete must not queue behind a request for the previous one —
 * a stalled load would hold the new calendar empty until the runner's stall
 * timeout let it through.
 */
const windowKey = (userID: string): string => `calendar-window:${userID}`

interface WindowLoad {
  owner: CalendarWindowRegistration
  weeks: readonly Date[]
  /**
   * Called when this load leaves the queue and the request actually goes out.
   * A caller that gives its records a deadline (the prefetch gate) must not
   * start counting while the load is still waiting its turn. A list, because
   * two queued loads merge into one and both callers are owed the signal.
   */
  onStart: readonly (() => void)[]
}

/**
 * Weeks are Date objects, and two registrations hold different objects for the
 * same Monday. Sorted, because everything downstream reads them as
 * chronological: contiguousWeekRuns splits into consecutive runs by comparing
 * with the previous week, so an out-of-order list becomes three one-week runs —
 * three health requests where the consolidation exists to make one.
 */
const unionWeeks = (left: readonly Date[], right: readonly Date[]): Date[] => {
  const byTime = new Map<number, Date>()
  for (const week of [...left, ...right]) byTime.set(week.getTime(), week)
  return [...byTime.values()].sort((a, b) => a.getTime() - b.getTime())
}

/**
 * A group covers a subset of the weeks and a refresh covers all of them, so
 * two queued loads combine into their union — collapsing to the later one
 * would drop the coverage of whichever was wider.
 *
 * Scrolling makes a NEW registration for the same athlete, and the queued load
 * may be the only thing that knows about a realtime change; keeping its weeks
 * matters, because the page itself only loads weeks it has not seen before.
 * The task below narrows whatever survives to what the window on screen
 * actually shows, so merging generously here cannot fetch anything stale.
 */
const mergeLoads = (queued: WindowLoad, incoming: WindowLoad): WindowLoad =>
  queued.owner.userID === incoming.owner.userID
    ? {
        owner: incoming.owner,
        weeks: unionWeeks(queued.weeks, incoming.weeks),
        onStart: [...queued.onStart, ...incoming.onStart],
      }
    : incoming

/**
 * Whether this window writes the scope's records: the same athlete (or the
 * signed-in user's own data, which several queries key without a userID) and
 * days it holds.
 */
const ownedByWindow = (window: CalendarWindowRegistration, scope: CalendarScope): boolean => {
  const sameAthlete = scope.userId === null ? window.isSelf : scope.userId === window.userID
  if (!sameAthlete) return false
  // A scope naming exact days describes one cache entry, and the window only
  // writes whole weeks: a one-day query shares no record with them, however
  // much its range overlaps.
  if (scope.dates !== undefined) {
    return window.weeks.some((week) => sameDays(weekDays(week), scope.dates ?? []))
  }
  return overlapsDays(scope, window.from, window.to)
}

const sameDays = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((day, index) => day === right[index])

/** The inclusive days of one week, in the `yyyy-MM-dd` the ranges use. */
const weekDays = (week: Date): string[] => getWeekDates(week)

/** The calendar page registers its window while mounted. */
export function registerCalendarWindow(registration: CalendarWindowRegistration): () => void {
  windows.push(registration)
  listeners.forEach((listener) => listener())
  // The same registration, as the records registry sees it: whoever needs a
  // refresh of these weeks asks there and never writes them directly.
  const unregisterOwner = registerRecordOwner({
    owns: (scope) => ownedByWindow(registration, scope),
    // THIS registration, not whichever window is current: `owns` answered for
    // this one, and delegating the refresh to the global current window meant a
    // caller could be told its records were refreshed by a window that no
    // longer holds them (two windows overlap while a tree is rebuilt, or the
    // user scrolled between the two calls).
    refresh: (scopes) => reloadWindow(registration, scopes),
  })
  return () => {
    unregisterOwner()
    const at = windows.lastIndexOf(registration)
    if (at === -1) return
    windows.splice(at, 1)
    listeners.forEach((listener) => listener())
  }
}

/**
 * Queues a load of exactly these weeks behind whatever the window is already
 * loading, and reports what that load did — `failed: []` when there was
 * nothing to do. A caller that asked for the refresh (the button) can show it;
 * background callers ignore it, and nothing here ever rejects.
 */
export function loadWeeks(
  weeks: readonly Date[],
  onStart?: () => void,
  /** The registration that asked, when it is not simply the current one. */
  asOwner?: CalendarWindowRegistration,
): Promise<RefreshOutcome> {
  const owner = asOwner ?? currentWindow()
  if (owner === null || weeks.length === 0) return Promise.resolve(NOTHING_TO_DO)
  return runSerial<WindowLoad, RefreshOutcome>(
    windowKey(owner.userID),
    { owner, weeks, onStart: onStart ? [onStart] : [] },
    (load, isStale) => {
      load.onStart.forEach((notify) => notify())
      const owner = currentWindow()
      // Nothing is on screen any more, or somebody else's calendar is: this
      // answer would be about weeks nobody shows and could overwrite what the
      // current window has just loaded.
      if (owner?.userID !== load.owner.userID) return Promise.resolve(NOTHING_TO_DO)
      if (owner === load.owner) return owner.load(load.weeks, isStale)
      // Same athlete, newer window (a scroll): load what it still shows. The
      // rest of the request is genuinely off screen and can be dropped — but
      // a realtime change for a week that stayed must not be dropped with it.
      const onScreen = new Set(owner.weeks.map((week) => week.getTime()))
      const weeks = load.weeks.filter((week) => onScreen.has(week.getTime()))
      return weeks.length > 0 ? owner.load(weeks, isStale) : Promise.resolve(NOTHING_TO_DO)
    },
    { merge: mergeLoads },
  ).then((result) => {
    // A run that threw is a failure of the whole load; one torn down by
    // navigation is not — nothing is wrong, and nothing was refreshed.
    if (result.status === "done") return result.value
    return result.status === "aborted"
      ? ABORTED
      : refreshFailed(["calendarItems", "basicRequirementsBatch", "healthMetricsSummary"])
  })
}

/**
 * Reloads the whole window when one of `ranges` touches the weeks it shows
 * (`null`: always) — the consolidated prefetch, one wide request per source,
 * never one per week. This is also the periodic refresh: the page ticks it on
 * an interval, so the weeks it owns need no polling of their own.
 */
function reloadWindow(
  window: CalendarWindowRegistration,
  ranges: readonly CalendarScope[] | null,
): Promise<RefreshOutcome> {
  if (ranges === null) return loadWeeks(window.weeks, undefined, window)

  const mine = ranges.filter((range) => ownedByWindow(window, range))
  if (mine.length === 0) return Promise.resolve(NOTHING_TO_DO)

  // Only the weeks the change actually touches. Reloading all thirteen for one
  // edited day is three wide requests where one would do — and the untouched
  // weeks would be re-read for nothing.
  const touched = window.weeks.filter((week) => {
    const [firstDay, lastDay] = dateSpan(weekDays(week))
    return mine.some((range) => overlapsDays(range, firstDay, lastDay))
  })
  return loadWeeks(touched.length > 0 ? touched : window.weeks, undefined, window)
}

export function reloadWindowIfTouched(ranges: readonly CalendarScope[] | null): Promise<RefreshOutcome> {
  const window = currentWindow()
  return window === null ? Promise.resolve(NOTHING_TO_DO) : reloadWindow(window, ranges)
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Whether the mounted window owns these days for this athlete — i.e. whether
 * its reload is the authoritative re-read of their records. "Is a window
 * mounted at all" is not that question: a window for another athlete, or for
 * other weeks, writes nothing of theirs.
 */
export function isCalendarWindowOwned(userID: string, dates: readonly string[]): boolean {
  const window = currentWindow()
  const from = dates[0] ?? ""
  const to = dates[dates.length - 1] ?? ""
  return from !== "" && window !== null && window.userID === userID && from >= window.from && to <= window.to
}

/**
 * For a query that would otherwise poll its own week: while a window owns
 * those days, its periodic reload is the refresh, and a second poller would
 * both duplicate the request and race the window's write. Screens with no
 * window — the plans page, a coach's multi-athlete list — get `false` and keep
 * polling exactly as before.
 */
export function useIsCalendarWindowOwned(userID: string, dates: readonly string[]): boolean {
  return useSyncExternalStore(subscribe, () => isCalendarWindowOwned(userID, dates))
}
